// Style profile logic for the home-page Style Quiz. No AI, no server cost —
// answers are hashed client-side into one of six curated profiles.

export const QUIZ_QUESTIONS = [
  {
    id: "aesthetic",
    prompt: "Which aesthetic feels most like you?",
    options: [
      { id: "traditional", label: "Traditional & regal", desc: "Rich textiles, gold, heritage" },
      { id: "modern", label: "Modern & minimal", desc: "Clean lines, negative space" },
      { id: "bohemian", label: "Garden & bohemian", desc: "Organic florals, greenery, natural light" },
      { id: "bollywood", label: "Cinematic & bold", desc: "Dramatic colour, film-set energy" },
    ],
  },
  {
    id: "scale",
    prompt: "How big is your celebration?",
    options: [
      { id: "intimate", label: "Intimate", desc: "Under 100 guests" },
      { id: "grand", label: "Grand", desc: "100 – 300 guests" },
      { id: "epic", label: "Epic", desc: "300 +" },
    ],
  },
  {
    id: "palette",
    prompt: "Which palette pulls at your heart?",
    options: [
      { id: "soft", label: "Blush & cream", desc: "Soft, romantic, whisper-light" },
      { id: "regal", label: "Emerald & burgundy", desc: "Deep, regal, wine-rich" },
      { id: "vibrant", label: "Marigold & scarlet", desc: "Vivid, celebratory, alive" },
      { id: "earthy", label: "Terracotta & sage", desc: "Earthy, organic, sun-warmed" },
    ],
  },
];

// Six curated profiles. Each maps to 4 portfolio image URLs already on the site.
export const PROFILES = {
  "modern-romantic": {
    name: "Modern Romantic",
    tagline: "Soft, editorial, timeless.",
    description:
      "Ivory drapery, cascading florals in blush and cream, sculptural candelabras. You want your day to feel like a Rêve editorial — clean, cinematic, and unforgettable.",
    keywords: ["Modern", "Intimate", "Blush palette", "Editorial"],
    images: [
      "https://i.imgur.com/rJS8Fvv.jpeg",
      "https://customer-assets-39nsmqrw.emergentagent.net/job_elegant-event-design-1/artifacts/60m0sfm1_dea6f43b-9664-4037-87bd-965dfa049d75.jpg",
      "https://i.imgur.com/Pg7YEOH.jpeg",
      "https://images.pexels.com/photos/34079355/pexels-photo-34079355.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
    ],
  },
  "regal-traditionalist": {
    name: "Regal Traditionalist",
    tagline: "Heritage, gold, gravitas.",
    description:
      "Grand mandaps in marigold and scarlet, brass and rich textiles, layered floral canopies. You want a day that honours your traditions in their fullest, richest expression.",
    keywords: ["Traditional", "Grand", "Regal palette", "Heritage"],
    images: [
      "https://images.pexels.com/photos/34079355/pexels-photo-34079355.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
      "https://i.imgur.com/zIldkrQ.jpeg",
      "https://images.pexels.com/photos/37331162/pexels-photo-37331162.jpeg",
      "https://i.imgur.com/ffM4f3A.jpeg",
    ],
  },
  "garden-bohemian": {
    name: "Garden Bohemian",
    tagline: "Wildflowers, sunset light, unhurried joy.",
    description:
      "Loose garden florals, terracotta and sage tones, hand-lettered signage under warm lantern light. Your celebration is a slow bloom — everyone breathes, everyone lingers.",
    keywords: ["Bohemian", "Intimate", "Earthy palette", "Outdoor"],
    images: [
      "https://i.imgur.com/hQevNHp.jpeg",
      "https://i.imgur.com/YPzrFh3.jpeg",
      "https://images.pexels.com/photos/17057034/pexels-photo-17057034.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
      "https://customer-assets-39nsmqrw.emergentagent.net/job_elegant-event-design-1/artifacts/l0xuevi6_0dabb0c8-01be-4fc2-a496-5d6aab910ff2.jpg",
    ],
  },
  "cinematic-grandeur": {
    name: "Cinematic Grandeur",
    tagline: "Bollywood-scale, spotlight-drenched, iconic.",
    description:
      "Dramatic staging, choreographed lighting, oversized floral installations. Your day should feel like the opening scene of a film — every guest a witness to something legendary.",
    keywords: ["Bollywood", "Epic", "Vibrant palette", "Cinematic"],
    images: [
      "https://i.imgur.com/zIldkrQ.jpeg",
      "https://images.pexels.com/photos/37331162/pexels-photo-37331162.jpeg",
      "https://customer-assets-39nsmqrw.emergentagent.net/job_elegant-event-design-1/artifacts/lw9j0fbw_a45ee8ca-3047-411f-ab5c-4ac9b707e70f.jpg",
      "https://i.imgur.com/rJS8Fvv.jpeg",
    ],
  },
  "heritage-elegance": {
    name: "Heritage Elegance",
    tagline: "Intimate, jewel-toned, quietly luxurious.",
    description:
      "Emerald velvets, burgundy florals, brass detailing. Your day is small on guest list but rich on ritual — every element carries weight, meaning and memory.",
    keywords: ["Traditional", "Intimate", "Regal palette", "Ritual"],
    images: [
      "https://customer-assets-39nsmqrw.emergentagent.net/job_elegant-event-design-1/artifacts/60m0sfm1_dea6f43b-9664-4037-87bd-965dfa049d75.jpg",
      "https://images.pexels.com/photos/17057034/pexels-photo-17057034.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
      "https://i.imgur.com/ffM4f3A.jpeg",
      "https://customer-assets-39nsmqrw.emergentagent.net/job_elegant-event-design-1/artifacts/lgwimkme_35b815f1-4e68-4ae1-9be0-adddf00bd463.jpg",
    ],
  },
  "whimsical-garden": {
    name: "Whimsical Garden",
    tagline: "Blooms, laughter, soft dusk light.",
    description:
      "Pastel florals, pampas grass, layered rugs, warm string lights. Your celebration is joyful and airy — big on love, big on colour, gentle on formality.",
    keywords: ["Bohemian", "Grand", "Soft palette", "Whimsical"],
    images: [
      "https://i.imgur.com/hQevNHp.jpeg",
      "https://i.imgur.com/rJS8Fvv.jpeg",
      "https://i.imgur.com/YPzrFh3.jpeg",
      "https://i.imgur.com/ClZeiEs.jpeg",
    ],
  },
};

// Deterministic profile resolver — every combination maps to exactly one profile.
export function resolveProfile(answers) {
  const a = answers.aesthetic;
  const s = answers.scale;
  const p = answers.palette;

  if (a === "modern") return "modern-romantic";
  if (a === "bollywood") return "cinematic-grandeur";
  if (a === "bohemian") return s === "intimate" ? "garden-bohemian" : "whimsical-garden";
  // traditional
  if (p === "regal") return s === "intimate" ? "heritage-elegance" : "regal-traditionalist";
  return "regal-traditionalist";
}
